import { InfoCircleOutlined, LineChartOutlined } from "@ant-design/icons"
import { Col, Row, Tooltip, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { InputNumber } from "../../../../components/Input/InputNumber.tsx"
import { budgetFormat } from "../../../../utils/number/budget-format.ts"
import { WorkspaceCard } from "../../../Lead/LeadWorkspacePage/cards/WorkspaceCard.tsx"
import { LeadOverviewField } from "../../../Lead/LeadWorkspacePage/overview/LeadOverviewField.tsx"
import { OverviewCardTitle } from "../../../Lead/LeadWorkspacePage/overview/OverviewCardIcon.tsx"
import { canViewFinanceTab } from "../../helpers/project-finance-visibility.ts"
import type { PermissionProjectPermissions } from "../../../SettingsPermissions/types/permissions.types.ts"
import type { Project, ProjectUpdateData } from "../../types/project.types.ts"

const { Text } = Typography

// project.types.ts predates margin_multiplier/margin_warning_percent being
// wired up, but the API has always returned both (BudgetSummarySection
// already watches them off the same initial project payload) — a read-side
// type fix, not a new field.
type ProjectWithMarginSettings = Project & {
  margin_multiplier?: number | null
  margin_warning_percent?: number | null
}

interface FinancialSummaryCardValues {
  sales_price: number | null
  planned_payable: number | null
  margin_multiplier: number | null
  margin_warning_percent: number | null
}

interface ProjectFinancialSummaryCardProps {
  project: ProjectWithMarginSettings
  projectPermissions?: PermissionProjectPermissions
  canEdit?: boolean
  onSave: (changed: Partial<ProjectUpdateData>) => Promise<boolean>
}

// Replaces the old Financial tab (BudgetSummarySection) outright — same
// fields, same client-computed Margin, same updateProject PATCH, new
// presentation. There is deliberately only ever one Financial card on this
// page; this is it.
function ProjectFinancialSummaryCard({
  project,
  projectPermissions,
  canEdit,
  onSave
}: ProjectFinancialSummaryCardProps) {
  const { t } = useTranslation()
  const financeAllowed = canViewFinanceTab(projectPermissions)

  const values: FinancialSummaryCardValues = {
    sales_price: project?.sales_price ?? null,
    planned_payable: project?.planned_payable ?? null,
    margin_multiplier: project?.margin_multiplier ?? null,
    margin_warning_percent: project?.margin_warning_percent ?? null
  }

  const margin =
    values.sales_price != null && values.planned_payable != null
      ? values.sales_price - values.planned_payable
      : null
  const hasNegativeMargin = margin != null && margin < 0

  return (
    <WorkspaceCard<FinancialSummaryCardValues>
      cardId={"project-financial-summary"}
      title={
        <OverviewCardTitle icon={<LineChartOutlined />} accent={"green"}>
          {t("projectWorkspace.cards.financial.title")}
        </OverviewCardTitle>
      }
      ariaTitle={t("projectWorkspace.cards.financial.title")}
      className={"h-full"}
      canEdit={canEdit && financeAllowed}
      values={values}
      onSave={onSave}
      renderEdit={({ values, setValues, saving }) => {
        const draftMargin =
          values.sales_price != null && values.planned_payable != null
            ? values.sales_price - values.planned_payable
            : null
        const draftNegative = draftMargin != null && draftMargin < 0

        return (
          <>
            <Row gutter={[16, 12]}>
              <Col xs={24} sm={8}>
                <LeadOverviewField
                  label={t("projectWorkspace.cards.financial.salesPrice")}
                >
                  <InputNumber
                    prefix={"$"}
                    placeholder={"0.00"}
                    step={0.01}
                    min={0}
                    precision={2}
                    style={{ width: "100%" }}
                    disabled={saving}
                    value={values.sales_price ?? undefined}
                    onChange={(next) =>
                      setValues({ sales_price: next ?? null })
                    }
                  />
                </LeadOverviewField>
              </Col>
              <Col xs={24} sm={8}>
                <LeadOverviewField
                  label={t("projectWorkspace.cards.financial.plannedPayable")}
                >
                  <InputNumber
                    prefix={"$"}
                    placeholder={"0.00"}
                    step={0.01}
                    min={0}
                    precision={2}
                    style={{ width: "100%" }}
                    disabled={saving}
                    status={draftNegative ? "warning" : undefined}
                    value={values.planned_payable ?? undefined}
                    onChange={(next) =>
                      setValues({ planned_payable: next ?? null })
                    }
                  />
                  {draftNegative && (
                    <Text type={"warning"} className={"text-xs"}>
                      {t(
                        "projectWorkspace.cards.financial.negativeMarginWarning"
                      )}
                    </Text>
                  )}
                </LeadOverviewField>
              </Col>
              <Col xs={24} sm={8}>
                <LeadOverviewField
                  label={t("projectWorkspace.cards.financial.marginAuto")}
                >
                  <InputNumber
                    prefix={"$"}
                    disabled
                    value={draftMargin ?? undefined}
                    status={draftNegative ? "warning" : undefined}
                    style={{ width: "100%" }}
                  />
                </LeadOverviewField>
              </Col>
            </Row>
            <Row gutter={[16, 12]} className={"mt-3"}>
              <Col xs={24} sm={8}>
                <LeadOverviewField
                  label={t("projectWorkspace.cards.financial.marginMultiplier")}
                  icon={
                    <Tooltip
                      title={t(
                        "projectWorkspace.cards.financial.marginMultiplierHint"
                      )}
                    >
                      <InfoCircleOutlined style={{ color: "#00000073" }} />
                    </Tooltip>
                  }
                >
                  <InputNumber
                    suffix={"x"}
                    placeholder={"3.00"}
                    step={0.1}
                    min={0}
                    precision={2}
                    style={{ width: "100%" }}
                    disabled={saving}
                    value={values.margin_multiplier ?? undefined}
                    onChange={(next) =>
                      setValues({ margin_multiplier: next ?? null })
                    }
                  />
                </LeadOverviewField>
              </Col>
              <Col xs={24} sm={8}>
                <LeadOverviewField
                  label={t(
                    "projectWorkspace.cards.financial.marginWarningPercent"
                  )}
                  icon={
                    <Tooltip
                      title={t(
                        "projectWorkspace.cards.financial.marginWarningHint"
                      )}
                    >
                      <InfoCircleOutlined style={{ color: "#00000073" }} />
                    </Tooltip>
                  }
                >
                  <InputNumber
                    suffix={"%"}
                    placeholder={"20"}
                    step={1}
                    min={0}
                    max={100}
                    precision={2}
                    style={{ width: "100%" }}
                    disabled={saving}
                    value={values.margin_warning_percent ?? undefined}
                    onChange={(next) =>
                      setValues({ margin_warning_percent: next ?? null })
                    }
                  />
                </LeadOverviewField>
              </Col>
            </Row>
          </>
        )
      }}
    >
      <Row gutter={[16, 12]}>
        <Col xs={24} sm={8}>
          <LeadOverviewField
            label={t("projectWorkspace.cards.financial.salesPrice")}
            value={budgetFormat(values.sales_price)}
          />
        </Col>
        <Col xs={24} sm={8}>
          <LeadOverviewField
            label={t("projectWorkspace.cards.financial.plannedPayable")}
            value={budgetFormat(values.planned_payable)}
          />
        </Col>
        <Col xs={24} sm={8}>
          <LeadOverviewField
            label={t("projectWorkspace.cards.financial.marginAuto")}
          >
            <Text
              className={hasNegativeMargin ? "text-warning" : "text-success"}
            >
              {budgetFormat(margin)}
            </Text>
          </LeadOverviewField>
        </Col>
        <Col xs={24} sm={8}>
          <LeadOverviewField
            label={t("projectWorkspace.cards.financial.marginMultiplier")}
            value={
              values.margin_multiplier != null
                ? `${values.margin_multiplier}x`
                : null
            }
          />
        </Col>
        <Col xs={24} sm={8}>
          <LeadOverviewField
            label={t("projectWorkspace.cards.financial.marginWarningPercent")}
            value={
              values.margin_warning_percent != null
                ? `${values.margin_warning_percent}%`
                : null
            }
          />
        </Col>
      </Row>
    </WorkspaceCard>
  )
}

export { ProjectFinancialSummaryCard }
